import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { useApp } from '@/contexts/AppContext';
import { ROLE_TEMPLATES, Permission } from '@/types/permissions';
import { getCurrentUser } from '@/api/services/userService';
import { getDemoRoles, subscribeToRoleChanges } from '@/utils/demoRolePersistence';
import { getRoleTemplateId } from '@/lib/permissions';

export function usePermissions() {
  const { currentUser, mode } = useApp();

  // State to trigger re-render when demo roles change
  const [roleVersion, setRoleVersion] = useState(0);

  // Subscribe to demo role changes in demo mode
  useEffect(() => {
    if (mode === 'demo') {
      const unsubscribe = subscribeToRoleChanges(() => {
        setRoleVersion(prev => prev + 1);
      });
      return unsubscribe;
    }
  }, [mode]);

  // Fetch user data in live mode
  const { data: userData, isLoading, error } = useQuery({
    queryKey: ['currentUser'],
    queryFn: getCurrentUser,
    enabled: mode === 'live',
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Suppress unused variable warning — roleVersion intentionally triggers re-render
  void roleVersion;

  // Determine permissions based on mode
  let permissions: Permission[] = [];
  let isSystemAdmin = false;
  let roleId: string | number | undefined;

  if (mode === 'live') {
    if (userData) {
      const apiPermissions = userData.role?.userPermissions || [];
      permissions = apiPermissions as Permission[];
      isSystemAdmin = userData.isSystemAdmin;
      roleId = userData.role?.id;
    }
  } else {
    // Demo mode: resolve role template from currentUser's forecasterRole
    const demoRoleId = getRoleTemplateId(currentUser.forecasterRole);
    roleId = demoRoleId;
    const demoRoles = getDemoRoles();
    const role = demoRoles.find(r => r.id === demoRoleId);
    permissions = role?.permissions || [];
    isSystemAdmin = currentUser.forecasterRole === 'System Administrator';
  }

  const hasPermission = (permission: Permission): boolean => {
    return permissions.includes(permission);
  };

  const hasAnyPermission = (...permissionsToCheck: Permission[]): boolean => {
    return permissionsToCheck.some(permission => permissions.includes(permission));
  };

  const hasAllPermissions = (...permissionsToCheck: Permission[]): boolean => {
    return permissionsToCheck.every(permission => permissions.includes(permission));
  };

  return {
    currentUser,
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSystemAdmin,
    roleId,
    isLoading: mode === 'live' ? isLoading : false,
    error: mode === 'live' ? error : null,
  };
}
